import { DateTime } from 'luxon';
import { CONVERSION_ATTRIBUTION_WINDOW_HOURS, type ConversionAttribution } from './schema';

/**
 * Whether a captured pair is still inside its window. Decided once, here, in
 * Luxon: `capturedAt` is ISO text on the wire and in storage, never a `Date`.
 * An unparsable stamp is not live — a corrupt record must age out, not stick.
 * A stamp in the future is live (clock skew between devices is not the
 * member's fault); the window only ever closes from the past side.
 */
export function isConversionAttributionLive(
  record: Pick<ConversionAttribution, 'capturedAt'>,
  now: string | DateTime,
): boolean {
  const captured = DateTime.fromISO(record.capturedAt, { setZone: true });
  const current = typeof now === 'string' ? DateTime.fromISO(now, { setZone: true }) : now;
  if (!captured.isValid || !current.isValid) return false;
  return current.diff(captured).as('hours') < CONVERSION_ATTRIBUTION_WINDOW_HOURS;
}

/**
 * The capture stamp, minted here so neither client formats a native `Date`:
 * an ISO-8601 UTC instant that the window above reads back.
 */
export function conversionAttributionCapturedAtNow(): string {
  const iso = DateTime.utc().toISO();
  // `toISO()` is null only for an invalid DateTime; `utc()` is never invalid.
  if (iso === null) throw new Error('DateTime.utc() produced an invalid instant');
  return iso;
}
